import React, { FunctionComponent, useEffect, useState } from 'react'
import { DisabledModal, PostCheckout, ProcessOrder } from '../../component'
import { CHECKOUT_FLOW_TYPE, postCheckoutFlowMessage, postRoktMessage } from '../../integrations/inappbrowser-messenger'
import { getOverlay } from './utils'
import { TrackingEvent } from '../../model/monitoring'
import { useCloseCheckout } from '../../utils/close'
import { usePersonalization } from '../../utils/personalization'
import { useDynamicTimeout } from '../../utils/timeout'
import { CheckoutStatus, PostCheckoutFlow, AssetId } from '../../model'

import clsx from 'clsx'
import styles from './styles.module.css'
import {
  useCheckoutStatus,
  useProcessing,
  useAppRouter,
  setDisplayProcessingModal,
  setSendEndTransactionMessage,
  useAppDispatch,
  useIsAppleRetailAppleStoreAppOrder,
} from '../../state'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { useShowProcessingModal } from './ProcessingModal.hooks'
import { withPreventBrowserNavigation } from '../withPreventBrowserNavigation'
import { Loader, useIsDarkMode } from '@afterpaytouch/core'
import { Route } from '../../config/router'

interface Props {
  hasDefaultTimeout?: boolean
  allowPostCheckout?: boolean
  isAPGiftCardFlow?: boolean
}

const ProcessingModalBase: FunctionComponent<Props> = ({ hasDefaultTimeout = false, allowPostCheckout = true, isAPGiftCardFlow }) => {
  const close = useCloseCheckout()
  const router = useAppRouter()
  const isAfterpayNormalFlow = useShowProcessingModal()
  const isDarkMode = useIsDarkMode()
  const isAfterpayAppGiftCardFlow = !isAfterpayNormalFlow || isAPGiftCardFlow
  const { postCheckout } = usePersonalization()
  const { endConfirmTime, elapsedConfirmTime } = useProcessing()
  const checkoutStatus = useCheckoutStatus()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const isClosed = checkoutStatus === CheckoutStatus.Closed
  const postCheckoutEnabled = postCheckout.enabled && allowPostCheckout
  const dispatch = useAppDispatch()
  const isAppleRetailAppleStoreAppOrder = useIsAppleRetailAppleStoreAppOrder()

  const [cachedSendEndTransactionMessage] = useState(!(postCheckoutEnabled && postCheckout.flow === PostCheckoutFlow.PostCapture))
  const [cachedPostCheckoutEnabled] = useState(postCheckoutEnabled)
  const [showLoader, setShowLoader] = useState(true)
  const [currentTimeout, setCurrentTimeout] = useState<number | null>(postCheckoutEnabled || hasDefaultTimeout ? postCheckout.timeout : null)

  const isPostCheckoutAssetCashAppOrderServicingMessage = cachedPostCheckoutEnabled && postCheckout.asset.id === AssetId.CashAppOrderServicingMessage202503
  const processingModalStyle = clsx(
    styles.loaderWrapper,
    cachedPostCheckoutEnabled || isAfterpayAppGiftCardFlow ? styles.normalLayout : styles.blur,
    isPostCheckoutAssetCashAppOrderServicingMessage && styles.centralized
  )
  const showAfterpayGiftCardLoader = isAfterpayAppGiftCardFlow && isClosed

  const handlePostcheckoutTimerChange = (newTimeout: number | null): void => {
    if (newTimeout !== null) {
      setCurrentTimeout(newTimeout)
    }
  }

  const handleStateChange = (shouldShowLoader: boolean): void => {
    setShowLoader(shouldShowLoader)
  }

  const runNativeAppEvent = (): void => {
    postRoktMessage()

    postCheckoutFlowMessage(CHECKOUT_FLOW_TYPE.ON_ORDER_COMPLETE)
  }

  useEffect(() => {
    runNativeAppEvent()
  }, [])

  useEffect(() => {
    if (endConfirmTime > 0 && elapsedConfirmTime !== undefined && !postCheckoutEnabled) {
      const totalProcessingTimeout = postCheckout.timeout !== null ? postCheckout.timeout - elapsedConfirmTime : 0
      setCurrentTimeout(totalProcessingTimeout > 0 ? totalProcessingTimeout : 0)
    }
  }, [endConfirmTime])

  useDynamicTimeout(async () => {
    if (isAppleRetailAppleStoreAppOrder) {
      dispatch(setSendEndTransactionMessage(cachedSendEndTransactionMessage))
      await router.push(Route.PURCHASE_SUCCESS)
      dispatch(setDisplayProcessingModal(false))
    } else {
      await close(cachedSendEndTransactionMessage)
    }
  }, currentTimeout)

  useEffect(() => {
    let timer
    if (isClosed) {
      timer = setTimeout(() => {
        logEvent(TrackingEvent.STUCK_AFTER_CLOSE)
      }, 10000)
    }
    return () => clearTimeout(timer)
  }, [isClosed])

  useEffect(() => {
    router.prefetch(Route.PURCHASE_SUCCESS)
  }, [])

  const renderProcessingModal = (): JSX.Element => (
    <>
      <div className={processingModalStyle} data-testid='processingModal'>
        {isAfterpayAppGiftCardFlow ? (
          <div>
            <Loader alignCenter />
            <TrackOnMount eventName={TrackingEvent.VIEWED_LOADING_ONLY_MODAL} eventProperties={{ showLoaderOnly: showAfterpayGiftCardLoader }} />
          </div>
        ) : (
          <>
            {!isPostCheckoutAssetCashAppOrderServicingMessage && <div className={styles.topSection}>{showLoader && <ProcessOrder />}</div>}
            <PostCheckout onTimerChange={handlePostcheckoutTimerChange} onStateChange={handleStateChange} />
          </>
        )}
      </div>
    </>
  )

  const overlay = getOverlay({
    isAfterpayNormalFlow,
    cachedPostCheckoutEnabled,
    isAfterpayAppGiftCardFlow: isAfterpayAppGiftCardFlow ?? false,
    isDarkMode,
  })

  return (
    <>
      <DisabledModal overlay={overlay}>{renderProcessingModal()}</DisabledModal>
      <TrackOnMount eventName={TrackingEvent.VIEWED_PROCESSING_MODAL} eventProperties={{ showLoaderOnly: showAfterpayGiftCardLoader }} />
    </>
  )
}

export const ProcessingModal = withPreventBrowserNavigation(ProcessingModalBase)
